import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { fetchDeviceLogs, exportDeviceLogs } from '../../services/apiService';

const LogDetailsModal = ({ open, onClose, organizationId, device, date }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Load logs when modal opens or pagination changes
  const loadLogs = useCallback(async () => {
    if (!device || !date || !open) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`📋 Loading logs for device ${device.serialNumber} on ${date}`);
      const response = await fetchDeviceLogs(
        organizationId,
        device.deviceId,
        date,
        page + 1, // API uses 1-based pagination
        rowsPerPage
      );

      setLogs(response.logs || []);
      setTotalCount(response.pagination?.total || 0);
      console.log(`✅ Loaded ${response.logs?.length || 0} log entries`);
    } catch (err) {
      console.error('❌ Error loading device logs:', err);
      setError('Failed to load device logs');
    } finally {
      setLoading(false);
    }
  }, [organizationId, device, date, page, rowsPerPage, open]);

  // Load logs when dependencies change
  useEffect(() => {
    if (open) {
      loadLogs();
    }
  }, [loadLogs, open]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      await exportDeviceLogs(organizationId, device.deviceId, date);
      console.log('✅ Logs exported successfully');
    } catch (err) {
      console.error('❌ Error exporting logs:', err);
      setError('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Get log level color
  const getLogLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'E':
      case 'ERROR':
        return 'error';
      case 'W':
      case 'WARNING':
        return 'warning';
      case 'I':
      case 'INFO':
        return 'info';
      case 'D':
      case 'DEBUG':
        return 'default';
      case 'T':
      case 'TRACE':
        return 'primary';
      case 'U':
      case 'USER':
        return 'success';
      default:
        return 'default';
    }
  };

  // Get log level display text
  const getLogLevelDisplay = (level) => {
    switch (level?.toUpperCase()) {
      case 'E':
        return 'ERROR';
      case 'W':
        return 'WARNING';
      case 'I':
        return 'INFO';
      case 'D':
        return 'DEBUG';
      case 'T':
        return 'TRACE';
      case 'U':
        return 'USER';
      default:
        return level || 'UNKNOWN';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <WalteroTypography variant="h6">
            Device Logs - {device?.serialNumber}
          </WalteroTypography>
          <WalteroTypography variant="body2" color="textSecondary">
            {device?.name || 'N/A'} • {date}
          </WalteroTypography>
        </Box>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <IconButton color="inherit" size="small" onClick={loadLogs}>
              <RefreshIcon />
            </IconButton>
          }>
            {error}
          </Alert>
        )}

        {!loading && !error && logs.length === 0 && (
          <Alert severity="info">
            No logs found for this device on {date}
          </Alert>
        )}

        {!loading && logs.length > 0 && (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="180">Timestamp</TableCell>
                    <TableCell width="100">Log Level</TableCell>
                    <TableCell width="150">Tag</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getLogLevelDisplay(log.logLevel)}
                          color={getLogLevelColor(log.logLevel)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {log.tag || '-'}
                      </TableCell>
                      <TableCell sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-word',
                        maxWidth: '500px'
                      }}>
                        {log.message || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[50, 100, 250, 500]}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <WalteroTypography variant="body2" color="textSecondary">
            Total logs: {totalCount.toLocaleString()}
          </WalteroTypography>
        </Box>
        <WalteroButton
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={loading || exporting || logs.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </WalteroButton>
        <WalteroButton variant="contained" onClick={onClose}>
          Close
        </WalteroButton>
      </DialogActions>
    </Dialog>
  );
};

export default LogDetailsModal;